/**
 * Conversión — one rule, hundreds of words, and the words it does not reach (TASKS T52, SPEC §5,
 * BRIEF <conversion_engine>).
 *
 * The card gives an English word and a rule the learner has unlocked, and asks for the Spanish. The
 * pleasure of the mode is that the rule was taught once and generates words the game never taught:
 * `-tion becomes -ción` plus `information` yields `información` without `información` ever having
 * been an item.
 *
 * ## The hazard is the mode, not a footnote
 *
 * A generative rule over-applies. `question` is not `cuestión`, `embarrassed` is not `embarazada`,
 * and a learner who has just been shown a rule that works ten times out of ten will reach for it the
 * eleventh. So hazards are **items**, drawn and scheduled like any other (`hazardItems`), and a
 * hazard card shows **the rule anyway**. Nothing on it warns him. That is deliberate and it is the
 * whole mechanism: a trap that announces itself is not practice at resisting anything, and the
 * feedback — which names what happened — is where the teaching lands.
 *
 * Pure. No DOM, no clock, no store.
 */

import type { ModeInput, Presentation } from './types';

/**
 * The help this card offers if asked (invariant 2 — asking marks the attempt hinted, T54).
 *
 * Two different hints, because the two cards are stuck in different ways. On a rule word the learner
 * has the rule and needs the stem, so he gets the first letter and the length. On a hazard he has
 * everything he needs *except* the knowledge that he is being trapped, so that is what the scaffold
 * gives — and it is a large hint, which is precisely why taking it costs the interval.
 */
export function conversionScaffold(item: { es: string }, isHazard: boolean): string | null {
  if (isHazard) return 'The rule does not reach this word.';
  const letters = [...item.es.trim()];
  const first = letters[0];
  if (!first) return null;
  return `Starts with "${first}" — ${letters.length === 1 ? '1 letter' : `${letters.length} letters`}.`;
}

/**
 * Build the conversión card.
 *
 * The rule is the instruction line and the English word is the prompt. There is no gloss: the prompt
 * is already English, and a second English line under it would be the same word twice.
 */
export function conversionPresentation(input: ModeInput): Presentation {
  const { item, extras } = input;
  const spec = extras.conversion;
  const isHazard = spec?.hazard !== undefined;
  const scaffold = conversionScaffold(item, isHazard);

  if (!spec) {
    // No rule resolved — a tier-4 item naming a rule no file declares, which the build check
    // refuses, or an item minted elsewhere. It is still due, so it is still asked (invariant 7).
    return {
      mode: 'conversion',
      instruction: 'Say it in Spanish',
      segments: [{ kind: 'text', text: item.en }],
      gloss: null,
      expected: item.es,
      scaffold,
      input: 'text',
      fieldLabel: 'Your answer',
      announce: `${item.en}. Type it in Spanish.`,
    };
  }

  return {
    mode: 'conversion',
    // Identical on a hazard card. See the note above: the trap has to be live.
    instruction: spec.rule,
    segments: [{ kind: 'text', text: item.en }],
    gloss: null,
    expected: item.es,
    scaffold,
    input: 'text',
    fieldLabel: 'The Spanish',
    announce: `${spec.rule}. ${item.en}.`,
  };
}
